import {
    Build,
    Component,
    ComponentInterface,
    Element,
    Event,
    EventEmitter,
    h,
    Host,
    Method,
    Prop,
    State,
    Watch
} from '@stencil/core';
import {
    debounceEvent,
    inheritAriaAttributes,
    inheritAttributes,
    setOrRemoveAttribute
} from '../../utils/helpers';
import {
    AutocompleteTypes,
    InputChangeEventDetail,
    TextFieldTypes,
    WcsInputSize,
    WcsInputAutocorrect,
    WcsInputEnterKeyHint,
    WcsInputInputMode,
    WcsInputState
} from './input-interface';
import { AriaAttributeName, MutableAriaAttribute } from "../../utils/mutable-aria-attribute";

const INPUT_INHERITED_ATTRS = ['tabindex', 'title'];

/**
 * The input component is a form control that accepts a single line of text.
 * Implementation mainly inspired from Ionic Input Component.
 *
 * ## Accessibility guidelines 💡
 * > - If you use wcs-input outside a wcs-form-field, you have to manage the label and the error message yourself.
 * > You can use the `aria-label` attribute to provide a label for screen readers but adds no visual label.
 * 
 * @cssprop --wcs-input-icon-color-default - default icon color when the input is not focused
 * @cssprop --wcs-input-icon-color-focus - icon color when the input is focused
 * @cssprop --wcs-input-icon-color-disabled - icon color when the input is disabled
 *
 * @cssprop --wcs-input-background-color - background color of the input
 *
 * @cssprop --wcs-input-border-radius-left - border radius of the left side of the input
 * @cssprop --wcs-input-border-radius-right - border radius of the right side of the input
 * 
 * @cssprop --wcs-input-border-width - border width of the input
 * @cssprop --wcs-input-border-width-focus - border width of the input when focused
 *
 * @cssprop --wcs-input-height-l - large height of the input
 * @cssprop --wcs-input-height-m - medium height of the input
 * @cssprop --wcs-input-height-s - small height of the input
 * @cssprop --wcs-input-font-size-l - large font size of the input
 * @cssprop --wcs-input-font-size-m - medium font size of the input
 * @cssprop --wcs-input-font-size-s - small font size of the input
 *
 * @cssprop --wcs-input-border-style-default - default border style of the input
 * @cssprop --wcs-input-border-style-focus - border style of the input when focused
 * 
 * @cssprop --wcs-input-prefix-suffix-background-color - background color of the suffix/prefix
 * @cssprop --wcs-input-prefix-suffix-color - color of the suffix/prefix
 * @cssprop --wcs-input-prefix-suffix-color-disabled - color of the suffix/prefix when the input is disabled
 * @cssprop --wcs-input-prefix-suffix-font-weight - font weight of the suffix/prefix
 *
 * @cssprop --wcs-input-border-color-default - default border color of the input when not focused
 * @cssprop --wcs-input-border-color-disabled - border color of the input when disabled
 * @cssprop --wcs-input-border-color-focus - border color of the input when focused
 * @cssprop --wcs-input-reveal-password-button-border-color-focus - border color of the show/hide password button when focused
 * @cssprop --wcs-input-border-color-error - border color of the input when in error state
 *
 * @cssprop --wcs-input-value-color - color of the input value
 * @cssprop --wcs-input-value-font-weight - font weight of the input value
 * 
 * @cssprop --wcs-input-placeholder-color - color of the input placeholder
 * @cssprop --wcs-input-placeholder-font-weight - font weight of the input placeholder
 * @cssprop --wcs-input-placeholder-font-style - font style of the input placeholder
 * 
 * @cssprop --wcs-input-text-color-disabled - color of the input when disabled
 *
 * @cssprop --wcs-input-padding-horizontal-s - horizontal padding of the input in small size
 * @cssprop --wcs-input-padding-horizontal-m - horizontal padding of the input in medium size
 * @cssprop --wcs-input-padding-horizontal-l - horizontal padding of the input in large size
 * 
 * @cssprop --wcs-input-gap - gap between text input and icon
 */
@Component({
    tag: 'wcs-input',
    styleUrl: 'input.scss',
    shadow: { delegatesFocus: true },
})
export class Input implements ComponentInterface, MutableAriaAttribute {
    @Element() private el!: HTMLElement;
    private nativeInput?: HTMLInputElement;
    private inputId = `wcs-input-${inputIds++}`;
    private inheritedAttributes: { [k: string]: any } = {};
    private iconPassword = "visibility";

    /**
     * This is required for a WebKit bug which requires us to
     * blur and focus an input to properly focus the input in
     * an item with delegatesFocus. It will no longer be needed
     * with iOS 14.
     *
     * @internal
     */
    @Prop() fireFocusEvents: boolean = true;

    @State() private passwordReveal: boolean = false;

    /**
     * If the value of the type attribute is `"file"`, then this attribute will indicate the types of files that the
     * server accepts, otherwise it will be ignored. The value must be a comma-separated list of unique content type specifiers.
     */
    @Prop() accept?: string;

    /**
     * Indicates whether and how the text value should be automatically capitalized as it is entered/edited by the user.
     * Available options: `"off"`, `"none"`, `"on"`, `"sentences"`, `"words"`, `"characters"`.
     */
    @Prop() autocapitalize = 'off';

    /**
     * Indicates whether the value of the control can be automatically completed by the browser.
     */
    @Prop() autocomplete: AutocompleteTypes = 'off';

    /**
     * Whether auto correction should be enabled when the user is entering/editing the text value.
     */
    @Prop() autocorrect: WcsInputAutocorrect = 'off';

    /**
     * This Boolean attribute lets you specify that a form control should have input focus when the page loads.
     */
    @Prop() autofocus: boolean = false;

    /**
     * Set the amount of time, in milliseconds, to wait to trigger the `wcsInput` event after each keystroke.
     * This also impacts form bindings such as `ngModel` or `v-model`.
     */
    @Prop() debounce = 0;

    @Watch('debounce')
    protected debounceChanged() {
        this.wcsInput = debounceEvent(this.wcsInput, this.debounce);
    }

    /**
     * Prefix displayed before the text field contents. This is not included in the value.
     */
    @Prop() prefixLabel: string;

    /**
     * Suffix displayed after the text field contents. This is not included in the value.
     */
    @Prop() suffixLabel: string;

    /**
     * If `true`, the user cannot interact with the input.
     */
    @Prop({ reflect: true }) disabled: boolean = false;

    /**
     * A hint to the browser for which enter key to display.
     */
    @Prop() enterkeyhint?: WcsInputEnterKeyHint;

    /**
     * Specify the size (height) of the input.
     */
    @Prop({reflect: true}) size: WcsInputSize = 'm';

    /**
     * Name of the material icon to add to the input
     */
    @Prop() icon: string;

    /**
     * A hint to the browser for which keyboard to display.
     */
    @Prop() inputmode?: WcsInputInputMode;

    /**
     * The maximum value, which must not be less than its minimum (min attribute) value.
     */
    @Prop() max?: string;

    /**
     * If the value of the type attribute is `text`, `email`, `search`, `password`, `tel`, or `url`, this attribute
     * specifies the maximum number of characters that the user can enter.
     */
    @Prop() maxlength?: number;

    /**
     * The minimum value, which must not be greater than its maximum (max attribute) value.
     */
    @Prop() min?: string;

    /**
     * If the value of the type attribute is `text`, `email`, `search`, `password`, `tel`, or `url`, this attribute
     * specifies the minimum number of characters that the user can enter.
     */
    @Prop() minlength?: number;

    /**
     * If `true`, the user can enter more than one value. This attribute applies when the type attribute is set to
     * `"email"` or `"file"`, otherwise it is ignored.
     */
    @Prop({ reflect: true }) multiple?: boolean;

    /**
     * The name of the control, which is submitted with the form data.
     */
    @Prop() name: string = this.inputId;

    @Prop() hidePasswordButtonAriaLabel: string = "Cacher le mot de passe";
    @Prop() showPasswordButtonAriaLabel: string = "Afficher le mot de passe";

    /**
     * A regular expression that the value is checked against. The pattern must match the entire value, not just some
     * subset. Use the title attribute to describe the pattern to help the user. This attribute applies when the value
     * of the type attribute is `"text"`, `"search"`, `"tel"`, `"url"`, `"email"`, `"date"`, or `"password"`, otherwise
     * it is ignored. When the type attribute is `"date"`, `pattern` will only be used in browsers that do not support
     * the `"date"` input type natively. See https://developer.mozilla.org/en-US/docs/Web/HTML/Element/input/date for
     * more information.
     */
    @Prop() pattern?: string;

    /**
     * Instructional text that shows before the input has a value.
     */
    @Prop() placeholder?: string | null;

    /**
     * If `true`, the user cannot modify the value.
     */
    @Prop({ reflect: true }) readonly: boolean = false;

    /**
     * If `true`, the user must fill in a value before submitting a form.
     */
    @Prop({ reflect: true }) required: boolean = false;

    /**
     * If `true`, the element will have its spelling and grammar checked.
     */
    @Prop() spellcheck: boolean = false;

    /**
     * Specifies the state of the input. By default the input is in an normal state but you can to set it to 'error'
     * state if the data given by the user is not valid.
     */
    @Prop({reflect: true}) state: WcsInputState = 'initial';

    /**
     * Works with the min and max attributes to limit the increments at which a value can be set.
     * Possible values are: `"any"` or a positive floating point number.
     */
    @Prop() step?: string;

    /**
     * The type of control to display. The default type is text.
     */
    @Prop() type: TextFieldTypes = 'text';

    /**
     * The value of the input.
     */
    @Prop({mutable: true}) value?: string | number | null = '';

    /**
     * Emitted when a keyboard input occurred. See https://developer.mozilla.org/en-US/docs/Web/Events/input
     */
    @Event() wcsInput!: EventEmitter<KeyboardEvent>;

    /**
     * Emitted when the value has changed. See https://developer.mozilla.org/en-US/docs/Web/Events/change
     */
    @Event() wcsChange!: EventEmitter<InputChangeEventDetail>;

    /**
     * Emitted when the input loses focus.
     */
    @Event() wcsBlur!: EventEmitter<FocusEvent>;

    /**
     * Emitted when the input has focus.
     */
    @Event() wcsFocus!: EventEmitter<FocusEvent>;

    componentWillLoad() {
        this.inheritedAttributes = {
            ...inheritAriaAttributes(this.el),
            ...inheritAttributes(this.el, INPUT_INHERITED_ATTRS)
        };
    }

    connectedCallback() {
        this.debounceChanged();
        if (Build.isBrowser) {
            document.dispatchEvent(new CustomEvent('wcsInputDidLoad', {
                detail: this.el
            }));
        }
    }

    disconnectedCallback() {
        if (Build.isBrowser) {
            document.dispatchEvent(new CustomEvent('wcsInputDidUnload', {
                detail: this.el
            }));
        }
    }

    /**
     * Sets blur on the native `input` in `wcs-input`. Use this method instead of the global
     * `input.blur()`.
     * @internal
     */
    @Method()
    async setBlur() {
        if (this.nativeInput) {
            this.nativeInput.blur();
        }
    }

    /**
     * Returns the native `<input>` element used under the hood.
     */
    @Method()
    getInputElement(): Promise<HTMLInputElement> {
        return Promise.resolve(this.nativeInput!);
    }

    @Method()
    async setAriaAttribute(attr: AriaAttributeName, value: string | null | undefined) {
        setOrRemoveAttribute(this.nativeInput, attr, value);
    }
    
    private getValueAsString(): string {
        return typeof this.value === 'number' ? this.value.toString() :
            (this.value || '').toString();
    }

    private onInput = (ev: Event) => {
        const input = ev.target as HTMLInputElement | null;
        if (input) {
            this.value = input.value || '';
        }
        this.wcsInput.emit(ev as KeyboardEvent);
    }

    private onChange = (_: Event) => {
        this.wcsChange.emit({value: this.nativeInput.value});
    }

    private onBlur = (ev: FocusEvent) => {
        if (this.fireFocusEvents) {
            this.wcsBlur.emit(ev);
        }
    }

    private onFocus = (ev: FocusEvent) => {
        if (this.fireFocusEvents) {
            this.wcsFocus.emit(ev);
        }
    }

    private passwordRevealIconClick(): void {
        this.passwordReveal = !this.passwordReveal;
    }

    @Watch('passwordReveal')
    onPasswordRevealChange(): void {
        this.iconPassword = this.passwordReveal ? 'visibility_off' : 'visibility';
    }

    render() {
        const value = this.getValueAsString();

        return (
            <Host
                aria-disabled={this.disabled ? 'true' : null}
                data-has-prefix={!!this.prefixLabel}
                data-has-suffix={!!this.suffixLabel}
            >
                {this.prefixLabel ? (<span class="prefix" part="prefix">{this.prefixLabel}</span>) : null}
                {this.icon ? (<wcs-mat-icon icon={this.icon} size="m"></wcs-mat-icon>) : null}
                <input
                    class="native-input"
                    ref={input => this.nativeInput = input}
                    disabled={this.disabled}
                    accept={this.accept}
                    autoCapitalize={this.autocapitalize}
                    autoComplete={this.autocomplete}
                    autoCorrect={this.autocorrect}
                    autoFocus={this.autofocus}
                    enterKeyHint={this.enterkeyhint}
                    inputMode={this.inputmode}
                    min={this.min}
                    max={this.max}
                    minLength={this.minlength}
                    maxLength={this.maxlength}
                    multiple={this.multiple}
                    name={this.name}
                    pattern={this.pattern}
                    placeholder={this.placeholder || ''}
                    readOnly={this.readonly}
                    required={this.required}
                    spellcheck={this.spellcheck}
                    step={this.step}
                    type={this.passwordReveal ? 'text' : this.type}
                    value={value}
                    onInput={this.onInput}
                    onChange={this.onChange}
                    onBlur={this.onBlur}
                    onFocus={this.onFocus}
                    {...this.inheritedAttributes}
                />
                {this.type === "password" ? (
                    <button title={this.passwordReveal ? this.hidePasswordButtonAriaLabel : this.showPasswordButtonAriaLabel} class="toggle_password" onClick={() => this.passwordRevealIconClick()}>
                        <wcs-mat-icon icon={this.iconPassword} size="m"></wcs-mat-icon>
                    </button>
                ) : null}
                {this.suffixLabel ? (<span class="suffix" part="suffix">{this.suffixLabel}</span>) : null}
            </Host>
        );
    }
}

let inputIds = 0;
